import { PageContainer } from '@/components/PageContainer';
import { MarkdownMessage } from '@astro/shared-ui';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { Text } from '@televet/kibble-ui/Text';
import { Textarea } from '@televet/kibble-ui/Textarea';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Box, VStack, HStack, Code } from '@televet/kibble-ui/chakra';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@televet/kibble-ui/Icon';

export const Route = createFileRoute(
  '/_authenticated/eval-runs/$runId/review/$conversationId'
)({
  component: RouteComponent,
  beforeLoad: () => ({
    pageTitle: 'Agent Eval Review',
  }),
});

function RouteComponent() {
  const { runId, conversationId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [rating, setRating] = useState<'thumbs_up' | 'thumbs_down' | null>(
    null
  );
  const [comment, setComment] = useState('');
  const [judgeInstructions, setJudgeInstructions] = useState('');
  const [judgeResult, setJudgeResult] = useState<{
    verdict: 'good' | 'bad';
    reasons: string[];
  } | null>(null);

  const { data, isLoading } = useQuery(
    trpc.agentEvalRun.getRunConversation.queryOptions({
      evalRunId: runId,
      conversationId,
    })
  );

  const { data: runData } = useQuery(
    trpc.agentEvalRun.getRun.queryOptions({ id: runId })
  );

  const { data: allConversationIds } = useQuery(
    trpc.agentEvalRun.getRunConversationIds.queryOptions({
      evalRunId: runId,
    })
  );

  // Load existing feedback into form state
  useEffect(() => {
    if (data?.feedback) {
      setRating(data.feedback.rating === 1 ? 'thumbs_up' : 'thumbs_down');
      setComment(data.feedback.comment || '');
    } else {
      setRating(null);
      setComment('');
    }
    setJudgeResult(null);
  }, [data?.feedback]);

  const { mutate: submitFeedback, isPending: isSubmitting } = useMutation(
    trpc.agentEvalRun.submitRunFeedback.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [['agentEvalRun']],
        });
      },
    })
  );

  const { mutateAsync: completeRun } = useMutation(
    trpc.agentEvalRun.completeRun.mutationOptions()
  );

  const { mutateAsync: generateActionItems } = useMutation(
    trpc.agentEvalRun.generateActionItems.mutationOptions()
  );

  const { mutate: judgeConversation, isPending: isJudging } = useMutation(
    trpc.agentEvalRun.judgeConversation.mutationOptions({
      onSuccess: (data) => {
        setJudgeResult(data);
      },
    })
  );

  const [isFinishing, setIsFinishing] = useState(false);

  const handleFinishRun = async () => {
    setIsFinishing(true);
    try {
      await completeRun({ id: runId });
      if (stats && stats.needsHelp > 0) {
        await generateActionItems({ evalRunId: runId });
      }
      queryClient.invalidateQueries({ queryKey: [['agentEvalRun']] });
      navigate({ to: `/eval-runs/${runId}` });
    } catch {
      setIsFinishing(false);
    }
  };

  const stats = runData?.stats;

  // Find current position in conversations list (uses unpaginated ID list)
  const currentIndex = allConversationIds?.indexOf(conversationId) ?? -1;
  const prevConversationId =
    currentIndex > 0 ? allConversationIds![currentIndex - 1] : null;
  const nextConversationId =
    allConversationIds && currentIndex < allConversationIds.length - 1
      ? allConversationIds[currentIndex + 1]
      : null;

  const handleUseJudgeFeedback = useCallback(() => {
    if (!judgeResult) return;
    const judgeRating = judgeResult.verdict === 'good' ? 'thumbs_up' as const : 'thumbs_down' as const;
    const judgeComment = judgeResult.reasons.map((r) => `- ${r}`).join('\n');
    setRating(judgeRating);
    setComment(judgeComment);
    submitFeedback({
      evalRunId: runId,
      conversationId,
      rating: judgeRating,
      comment: judgeComment,
    });
  }, [judgeResult, runId, conversationId, submitFeedback]);

  if (isLoading) {
    return (
      <PageContainer>
        <Text>Loading conversation...</Text>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer>
        <VStack gap={4}>
          <Text variant="danger">Conversation not found</Text>
          <Button onClick={() => navigate({ to: `/eval-runs/${runId}` })}>
            Back to Eval
          </Button>
        </VStack>
      </PageContainer>
    );
  }

  const { conversation, evalRunConversation } = data;

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getRoleTagVariant = (role: string) => {
    switch (role.toLowerCase()) {
      case 'user':
        return 'info';
      case 'agent':
        return 'success';
      default:
        return 'default';
    }
  };

  const commentWordCount = comment.trim().split(/\s+/).filter(Boolean).length;
  const needsComment =
    rating === 'thumbs_down' && commentWordCount < 3;

  const handleSubmit = () => {
    if (!rating || needsComment) return;
    submitFeedback({
      evalRunId: runId,
      conversationId,
      rating,
      comment: comment || undefined,
    });
  };

  return (
    <Flex direction="column" flex="1 1 auto" overflow="hidden">
      {/* Sticky Navigation Header */}
      <Flex
        direction="column"
        bg="bg"
        borderBottom="1px solid"
        borderColor="border.default"
        position="sticky"
        top={0}
        zIndex={10}
        flexShrink={0}
      >
        {/* Top row: back + nav + actions */}
        <Flex justify="space-between" align="center" px={8} py={2}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate({ to: `/eval-runs/${runId}` })}
          >
            ← Back to Eval
          </Button>

          <HStack gap={2}>
            {currentIndex >= 0 && !!allConversationIds && (
              <Text fontSize="sm" variant="subtle">
                {currentIndex + 1} of {allConversationIds.length}
              </Text>
            )}
            <Button
              size="sm"
              variant="secondary"
              disabled={!prevConversationId}
              onClick={() =>
                prevConversationId &&
                navigate({
                  to: `/eval-runs/${runId}/review/${prevConversationId}`,
                })
              }
              iconName="arrowLeft"
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!nextConversationId}
              onClick={() =>
                nextConversationId &&
                navigate({
                  to: `/eval-runs/${runId}/review/${nextConversationId}`,
                })
              }
              iconName="arrowRight"
            >
              Next
            </Button>
            {runData?.run.status === 'InProgress' && (
              <Button
                size="sm"
                variant="primaryAccent"
                loading={isFinishing}
                loadingText="Finishing Eval..."
                onClick={handleFinishRun}
                iconName="checkmarkCircle"
              >
                Finish Eval
              </Button>
            )}
          </HStack>
        </Flex>

        {/* Bottom row: stats */}
        {stats && (
          <Flex
            px={8}
            py={1.5}
            gap={6}
            align="center"
            borderTop="1px solid"
            borderColor="border.subtle"
            bg="bg.subtle"
          >
            <HStack gap={1.5}>
              <Text fontSize="xs" variant="subtle">Reviewed</Text>
              <Tag label={String(stats.reviewed)} variant="success" />
            </HStack>
            <HStack gap={1.5}>
              <Text fontSize="xs" variant="subtle">Remaining</Text>
              <Tag label={String(stats.remaining)} variant="warning" />
            </HStack>
            <HStack gap={1.5}>
              <Icon name="thumbsUp" size="xs" color="text.success" />
              <Text fontSize="xs" fontWeight="medium" variant="success">
                {stats.reviewed - stats.needsHelp}
              </Text>
            </HStack>
            <HStack gap={1.5}>
              <Icon name="thumbsDown" size="xs" color="text.danger" />
              <Text fontSize="xs" fontWeight="medium" variant="danger">
                {stats.needsHelp}
              </Text>
            </HStack>
            <Text fontSize="xs" variant="subtle">
              {stats.total} total
            </Text>
          </Flex>
        )}

      </Flex>

      {/* Floating Feedback Panel - positioned right below sticky nav */}
      <Box
        position="fixed"
        top="110px"
        right="16px"
        w="320px"
        zIndex={999}
        display={{ base: 'none', lg: 'block' }}
      >
        <VStack gap={3} align="stretch">
          {/* Feedback Card */}
          <Card borderColor="border.default" rounded="lg" shadow="md" bg="bg">
            <Box px={6} pt={6} pb={2}>
              <Heading size="sm">Feedback</Heading>
            </Box>
            <Box px={6} pb={6}>
              <VStack gap={4} align="stretch">
                {/* Rating */}
                <HStack gap={2}>
                  <Button
                    size="md"
                    variant={rating === 'thumbs_up' ? 'primaryAccent' : 'secondary'}
                    onClick={() => setRating('thumbs_up')}
                    flex="1"
                    iconName="thumbsUp"
                  >
                    Good
                  </Button>
                  <Button
                    size="md"
                    variant={rating === 'thumbs_down' ? 'primaryDestructive' : 'secondaryDestructive'}
                    onClick={() => setRating('thumbs_down')}
                    flex="1"
                    iconName="thumbsDown"
                  >
                    Bad
                  </Button>
                </HStack>

                {/* Comment */}
                <Flex direction="column" gap={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Comment{rating === 'thumbs_down' && ' (required)'}
                  </Text>
                  <Textarea
                    placeholder={
                      rating === 'thumbs_down'
                        ? 'Explain why this is bad (at least 3 words)...'
                        : 'Add notes about this conversation...'
                    }
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    fontSize="sm"
                  />
                  {needsComment && comment.length > 0 && (
                    <Text fontSize="xs" variant="danger">
                      Comment must be at least 3 words
                    </Text>
                  )}
                </Flex>

                {/* Submit */}
                <Button
                  variant="primary"
                  disabled={!rating || needsComment}
                  loading={isSubmitting}
                  loadingText="Submitting..."
                  onClick={handleSubmit}
                >
                  {evalRunConversation.reviewed
                    ? 'Update Feedback'
                    : 'Submit Feedback'}
                </Button>
              </VStack>
            </Box>
          </Card>

          {/* AI Judge Card */}
          <Card borderColor="border.default" rounded="lg" shadow="md" bg="bg">
            <Box px={6} pt={6} pb={2}>
              <HStack gap={2}>
                <Icon name="magic" />
                <Heading size="sm">AI Judge</Heading>
              </HStack>
            </Box>
            <Box px={6} pb={6}>
              <VStack gap={3} align="stretch">
                <Textarea
                  placeholder="Additional instructions (optional)..."
                  value={judgeInstructions}
                  onChange={(e) => setJudgeInstructions(e.target.value)}
                  rows={2}
                  fontSize="sm"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  loading={isJudging}
                  loadingText="Judging..."
                  onClick={() =>
                    judgeConversation({
                      evalRunId: runId,
                      conversationId,
                      additionalInstructions: judgeInstructions || undefined,
                    })
                  }
                  iconName="magic"
                >
                  Run AI Judge
                </Button>

                {judgeResult && (
                  <Box>
                    <HStack gap={2} mb={2}>
                      <Tag
                        label={judgeResult.verdict === 'good' ? 'Good' : 'Bad'}
                        variant={judgeResult.verdict === 'good' ? 'success' : 'error'}
                      />
                      <Button
                        size="xs"
                        variant="secondary"
                        disabled={isSubmitting}
                        onClick={handleUseJudgeFeedback}
                      >
                        Use as Feedback
                      </Button>
                    </HStack>
                    <VStack gap={1} align="stretch">
                      {judgeResult.reasons.map((reason, i) => (
                        <Text key={i} fontSize="xs" variant="subtle">
                          &bull; {reason}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>
          </Card>
        </VStack>
      </Box>

    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        {/* Conversation Info */}
        <Card borderColor="border.default" rounded="lg">
          <Box px={6} pt={6} pb={3}>
            <Flex direction="column" gap={2}>
              <Flex align="center" gap={3}>
                <Tag label={conversation.currentAgent.name} variant="info" />
                <Tag
                  label={evalRunConversation.reviewed ? 'Reviewed' : 'Pending'}
                  variant={evalRunConversation.reviewed ? 'success' : 'default'}
                />
              </Flex>
              {conversation.aiSummary && (
                <Text fontSize="sm" variant="subtle">
                  {conversation.aiSummary}
                </Text>
              )}
              <Text fontSize="xs" variant="subtle">
                Created: {formatDate(conversation.createdAt)}
              </Text>
            </Flex>
          </Box>
        </Card>

        <Flex gap={4} direction="column">
          {/* Messages */}
          <Flex flex="1" direction="column" gap={3} minW="0" pr={{ lg: '340px' }}>
            <Heading size="sm">Messages</Heading>
            <VStack gap={3} align="stretch">
              {conversation.messages.length > 0 ? (
                conversation.messages.map((message) => (
                  <Card
                    key={message.id}
                    borderColor="border.default"
                    rounded="lg"
                    bg={message.role === 'user' ? 'bg.subtle' : 'bg.muted'}
                  >
                    <Box px={6} pt={6} pb={2}>
                      <HStack gap={4} justify="space-between">
                        <Flex align="center" gap={3}>
                          <Tag
                            label={message.role.toUpperCase()}
                            variant={getRoleTagVariant(message.role)}
                          />
                          {message.user && (
                            <Text fontSize="xs" variant="subtle">
                              {message.user.firstName} {message.user.lastName}
                            </Text>
                          )}
                          {message.agent && (
                            <Text fontSize="xs" variant="subtle">
                              Agent: {message.agent.name}
                            </Text>
                          )}
                        </Flex>
                        <Text fontSize="xs" variant="subtle">
                          {formatDate(message.createdAt)}
                        </Text>
                      </HStack>
                    </Box>
                    <Box px={6} pb={6}>
                      <MarkdownMessage content={message.message} />

                      {Array.isArray(message.toolCalls) &&
                        message.toolCalls.length > 0 && (
                          <Box mt={4}>
                            <Text fontSize="xs" fontWeight="bold" mb={2}>
                              Tool Calls:
                            </Text>
                            <VStack gap={2} align="stretch">
                              {message.toolCalls.map((toolCall: any) => (
                                <Box
                                  key={toolCall.id}
                                  p={2}
                                  bg="bg"
                                  rounded="sm"
                                  borderLeft="2px solid"
                                  borderLeftColor="border.emphasized"
                                >
                                  <HStack justify="space-between">
                                    <Text fontSize="xs" fontWeight="medium">
                                      {toolCall.tool?.name || 'Tool Call'}
                                    </Text>
                                    <Text fontSize="xs" variant="subtle">
                                      {toolCall.timeSpentInMilliseconds}ms
                                    </Text>
                                  </HStack>
                                  {toolCall.content && (
                                    <Box
                                      mt={1}
                                      maxH="200px"
                                      overflowY="auto"
                                      fontSize="xs"
                                    >
                                      <Code
                                        display="block"
                                        whiteSpace="pre"
                                        p={2}
                                        borderRadius="md"
                                        bg="bg.muted"
                                        color="text"
                                        overflowX="auto"
                                      >
                                        {typeof toolCall.content === 'string'
                                          ? toolCall.content
                                          : JSON.stringify(
                                              toolCall.content,
                                              null,
                                              2
                                            )}
                                      </Code>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        )}
                    </Box>
                  </Card>
                ))
              ) : (
                <Text variant="subtle">No messages in this conversation</Text>
              )}
            </VStack>
          </Flex>

        </Flex>
      </Flex>
    </PageContainer>
    </Flex>
  );
}
